/**
 * Datový model včelařské aplikace (verze schématu 1).
 *
 * Identifikátory v kódu jsou anglicky, české pojmy jsou v komentářích
 * a ve slovníčku v docs/datovy-model.md.
 *
 * Všechna data žijí lokálně v IndexedDB telefonu (offline-first PWA).
 * Proto:
 *  - ID jsou UUID generovaná v zařízení (crypto.randomUUID) – zálohy
 *    a případná budoucí synchronizace pak nemají kolize,
 *  - nic se fyzicky nemaže, jen se nastaví `deletedAt` (koš / obnova),
 *  - každý záznam nese `createdAt` / `updatedAt`.
 */

export const SCHEMA_VERSION = 2;

/** UUID v4 */
export type ID = string;
/** Kalendářní datum bez času, `YYYY-MM-DD` (zásahy se evidují po dnech). */
export type ISODate = string;
/** Okamžik v ISO 8601, `2026-10-08T21:55:00.000Z`. */
export type ISODateTime = string;

export interface BaseEntity {
  id: ID;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
  /** Měkké smazání – záznam je „v koši“. */
  deletedAt?: ISODateTime | null;
}

export interface GeoPoint {
  lat: number;
  lng: number;
}

// ---------------------------------------------------------------------------
// Stanoviště
// ---------------------------------------------------------------------------

/** Stanoviště (včelnice) – místo, kde stojí včelstva. */
export interface Apiary extends BaseEntity {
  name: string;
  /** Registrační číslo stanoviště v ústřední evidenci (ČMSCH). */
  registrationNumber?: string;
  /** Katastrální území / obec – hodí se pro hlášení a nákazové zóny. */
  cadastralArea?: string;
  location?: GeoPoint;
  note?: string;
  /** Zrušené stanoviště zůstává kvůli historii, jen se skryje. */
  archived: boolean;
}

// ---------------------------------------------------------------------------
// Včelstvo
// ---------------------------------------------------------------------------

export type HiveSystem =
  | 'langstroth'
  | 'dadant'
  | 'optimal'
  | 'adamec' // 39 × 24
  | 'budecak'
  | 'other';

export type ColonyStatus =
  | 'active' // žije
  | 'dead' // uhynulo
  | 'merged' // spojeno s jiným
  | 'sold' // prodáno
  | 'absconded'; // vylétlo / zmizelo

export type ColonyOrigin =
  | 'split' // oddělek
  | 'swarm' // chycený roj
  | 'shook_swarm' // smetenec
  | 'package' // balíček
  | 'purchased' // koupené včelstvo
  | 'unknown';

/**
 * Včelstvo. Úl (fyzická bedna) zatím není samostatná entita – včelstvo
 * nese označení úlu a jeho systém. Pokud se ukáže potřeba evidovat úly
 * jako majetek, přidá se tabulka `hives` ve verzi 2.
 */
export interface Colony extends BaseEntity {
  /** Aktuální stanoviště. Historie přesunů je v událostech `move`. */
  apiaryId: ID;
  /** Číslo / označení úlu, např. „7“ nebo „B3“. Unikátní v rámci stanoviště. */
  label: string;
  hiveSystem: HiveSystem;
  /** Rámková míra jako text, např. „39 × 24“, „Langstroth 1/2“. */
  frameSize?: string;
  /** Počet plodišťových nástavků (aktualizuje se z prohlídky). */
  boxCount?: number;
  /** Medníky aktuálně nasazené na úlu (mění se zápisem „Medníky“). */
  supers?: HoneySuper[];
  status: ColonyStatus;
  /** Kdy nastal aktuální status (např. datum úhynu). */
  statusDate?: ISODate;
  origin: ColonyOrigin;
  originDate?: ISODate;
  /** Mateřské včelstvo u oddělku / roje. */
  parentColonyId?: ID | null;
  /** Aktuální matka. Historie je v tabulce `queens`. */
  currentQueenId?: ID | null;
  /** Barva/štítek na mapě stanoviště, volitelné. */
  color?: string;
  note?: string;
}

/** Typ medníku. */
export type SuperType =
  | 'low' // nízký nástavek (např. 39 × 17)
  | 'half' // polonástavek
  | 'full' // plný nástavek (stejný jako plodišťový)
  | 'other';

export interface HoneySuper {
  id: ID;
  type: SuperType;
  /** Datum nasazení. */
  addedAt: ISODate;
  note?: string;
}

// ---------------------------------------------------------------------------
// Matka
// ---------------------------------------------------------------------------

export type QueenOrigin =
  | 'own_rearing' // vlastní chov
  | 'purchased' // koupená (od chovatele)
  | 'swarm_cell' // z rojového matečníku
  | 'supersedure' // tichá výměna
  | 'emergency' // nouzový matečník
  | 'unknown';

export type QueenStatus =
  | 'present' // ve včelstvu
  | 'caged' // v kleci / přidává se
  | 'replaced' // vyměněná
  | 'lost' // ztracená / nenalezena
  | 'dead';

export interface Queen extends BaseEntity {
  /** Včelstvo, ve kterém matka je (nebo naposledy byla). */
  colonyId?: ID | null;
  /** Rok vylíhnutí – určuje barvu značky. */
  year: number;
  marked: boolean;
  clipped: boolean;
  origin: QueenOrigin;
  /** Linie / kmen, např. „Vigor“, „Singer“, „Troiseck“. */
  line?: string;
  /** Chovatel / původ, číslo matky z chovu. */
  breeder?: string;
  status: QueenStatus;
  /** Barva skutečně použité značky; když chybí, odvodí se z roku. */
  markColor?: QueenMarkColor;
  introducedAt?: ISODate;
  removedAt?: ISODate;
  note?: string;
}

/** Mezinárodní barevné značení matek podle poslední číslice roku. */
export type QueenMarkColor = 'white' | 'yellow' | 'red' | 'green' | 'blue';

export function queenMarkColor(year: number): QueenMarkColor {
  const colors: QueenMarkColor[] = ['blue', 'white', 'yellow', 'red', 'green'];
  return colors[((year % 5) + 5) % 5];
}

/** Barva matky k zobrazení: ručně zvolená, jinak podle roku. */
export function queenColor(q: Pick<Queen, 'year' | 'markColor'>): QueenMarkColor {
  return q.markColor ?? queenMarkColor(q.year);
}

// ---------------------------------------------------------------------------
// Přípravky (číselník pro léčení)
// ---------------------------------------------------------------------------

export type TreatmentMethod =
  | 'fumigation' // fumigace (kouř)
  | 'aerosol' // aerosol (M-1 AER apod.)
  | 'evaporation' // odpařování (kys. mravenčí, thymol)
  | 'trickling' // pokapávání
  | 'sublimation' // sublimace (kys. šťavelová)
  | 'strips' // proužky / pásky
  | 'other';

export interface Product extends BaseEntity {
  name: string;
  activeSubstance?: string;
  method?: TreatmentMethod;
  /** Jednotka dávky, např. „ml“, „g“, „pásek“, „tableta“. */
  unit?: string;
  /** Ochranná lhůta ve dnech (doplňuje uživatel dle příbalového letáku). */
  withdrawalDays?: number;
  /** Předvyplněný přípravek (lze skrýt, ne smazat). */
  builtin: boolean;
  hidden: boolean;
  note?: string;
}

// ---------------------------------------------------------------------------
// Události (zásahy)
// ---------------------------------------------------------------------------

/**
 * Všechny zásahy jsou v jedné tabulce `events` jako diskriminované sjednocení
 * podle `type`. Jedna událost se může týkat více včelstev najednou
 * (typicky léčení nebo krmení celého stanoviště) – proto `colonyIds`.
 */
interface EventBase extends BaseEntity {
  date: ISODate;
  /** Dotčená včelstva (multiEntry index). */
  colonyIds: ID[];
  /** Stanoviště v okamžiku zásahu – pro filtrování a hlášení. */
  apiaryId: ID;
  note?: string;
  /** Kdo zásah provedl (u léčení povinný údaj evidence). */
  performedBy?: string;
  /** Teplota vzduchu °C – hodí se u léčení i prohlídek. */
  temperatureC?: number;
  /**
   * Společné ID záznamů vzniklých jedním zápisem pro více včelstev
   * (např. léčení celého stanoviště). Každé včelstvo má vlastní záznam,
   * batchId je jen spojuje pro evidenci léčiv.
   */
  batchId?: ID;
}

/** Hodnocení 1–5 (5 = nejlepší / nejvíc). */
export type Rating = 1 | 2 | 3 | 4 | 5;

export type QueenCells = 'none' | 'swarm' | 'supersedure' | 'emergency';

export interface InspectionEvent extends EventBase {
  type: 'inspection';
  queenSeen?: boolean;
  eggsSeen?: boolean;
  /** Počet obsazených uliček. */
  occupiedSeams?: number;
  /** Počet plástů s plodem. */
  broodFrames?: number;
  broodPattern?: Rating;
  temperament?: Rating;
  /** Odhad zásob v kg. */
  storesKg?: number;
  queenCells?: QueenCells;
  /** Přidané / odebrané nástavky, mezistěny apod. stručně. */
  actions?: string;
  /** Počet plodišťových nástavků po prohlídce – aktualizuje Colony.boxCount. */
  boxCountAfter?: number;
  diseaseSuspicion?: boolean;
}

export interface TreatmentEvent extends EventBase {
  type: 'treatment';
  productId: ID;
  /** Kopie názvu přípravku – evidence musí přežít úpravu číselníku. */
  productName: string;
  /** Číslo šarže – povinné pro evidenci použití veterinárních léčiv. */
  batchNumber?: string;
  method: TreatmentMethod;
  /** Dávka na jedno včelstvo. */
  dosePerColony?: number;
  unit?: string;
  /** Konec aplikace (vyjmutí pásků, konec odpařování). */
  endDate?: ISODate;
  /** Kopie ochranné lhůty v době aplikace. */
  withdrawalDays?: number;
  /** Pořadí ošetření v sérii (např. 1. ze 3 fumigací). */
  seriesIndex?: number;
  seriesTotal?: number;
}

export type VarroaMethod =
  | 'bottom_board' // podložka (přirozený spad)
  | 'alcohol_wash' // alkoholový test
  | 'sugar_roll' // cukrový test
  | 'post_treatment' // spad po ošetření
  | 'winter_debris'; // zimní měl odebraná k laboratornímu vyšetření (výsledek se doplní později)

export interface VarroaCheckEvent extends EventBase {
  type: 'varroa_check';
  method: VarroaMethod;
  miteCount: number;
  /** U podložky: kolik dní ležela. Spad za den = miteCount / days. */
  days?: number;
  /** U testů: velikost vzorku včel (obvykle ~300). */
  sampleSize?: number;
  /** Zimní měl odeslaný k vyšetření. */
  sentToLab?: boolean;
}

export type FeedType =
  | 'syrup_3_2' // cukerný roztok 3:2
  | 'syrup_1_1' // 1:1 (podněcovací)
  | 'invert_syrup' // invertní sirup
  | 'fondant' // pogača / cukrovo-medové těsto
  | 'other';

export interface FeedingEvent extends EventBase {
  type: 'feeding';
  feedType: FeedType;
  /** Množství na jedno včelstvo. */
  amountPerColony: number;
  unit: 'kg' | 'l';
}

export type HoneyType =
  | 'flower' // květový
  | 'forest' // lesní (medovicový)
  | 'mixed' // smíšený
  | 'rapeseed' // řepkový
  | 'acacia' // akátový
  | 'linden' // lipový
  | 'other';

export interface HarvestEvent extends EventBase {
  type: 'harvest';
  /** Celkem vytočeno za všechna `colonyIds`. */
  honeyKg: number;
  /** Volitelné rozpadnutí na jednotlivá včelstva. */
  perColonyKg?: Record<ID, number>;
  honeyType: HoneyType;
  framesExtracted?: number;
  /** Obsah vody v % (refraktometr). */
  waterPercent?: number;
  /** Označení šarže medu (pro prodej). */
  honeyBatch?: string;
}

export interface QueenChangeEvent extends EventBase {
  type: 'queen_change';
  oldQueenId?: ID | null;
  newQueenId?: ID | null;
  reason?: 'old' | 'poor_performance' | 'aggressive' | 'lost' | 'requeening' | 'other';
}

export interface SplitEvent extends EventBase {
  type: 'split';
  /** colonyIds[0] = mateřské včelstvo, newColonyId = vzniklý oddělek. */
  newColonyId: ID;
  framesTaken?: number;
}

export interface SwarmEvent extends EventBase {
  type: 'swarm';
  /** Pokud byl roj chycen a usazen. */
  caughtColonyId?: ID | null;
}

export interface MoveEvent extends EventBase {
  type: 'move';
  fromApiaryId: ID;
  toApiaryId: ID;
}

export interface StatusChangeEvent extends EventBase {
  type: 'status_change';
  newStatus: ColonyStatus;
  /** U úhynu / spojení: příčina, s kým spojeno apod. */
  cause?: string;
  mergedIntoColonyId?: ID | null;
}

/** Nasazení nebo odebrání medníků. */
export interface SuperChangeEvent extends EventBase {
  type: 'super_change';
  action: 'add' | 'remove';
  superType: SuperType;
  count: number;
}

export interface NoteEvent extends EventBase {
  type: 'note';
}

export type ColonyEvent =
  | InspectionEvent
  | TreatmentEvent
  | VarroaCheckEvent
  | FeedingEvent
  | HarvestEvent
  | QueenChangeEvent
  | SplitEvent
  | SwarmEvent
  | MoveEvent
  | StatusChangeEvent
  | SuperChangeEvent
  | NoteEvent;

export type EventType = ColonyEvent['type'];

// ---------------------------------------------------------------------------
// Nastavení
// ---------------------------------------------------------------------------

export interface BeekeeperProfile {
  name?: string;
  /** Registrační číslo chovatele (ČMSCH). */
  registrationNumber?: string;
  address?: string;
}

export interface Setting<T = unknown> {
  key: string;
  value: T;
}

// ---------------------------------------------------------------------------
// Záloha (export / import JSON)
// ---------------------------------------------------------------------------

export const BACKUP_FORMAT = 'vcelarska-aplikace-zaloha';

export interface Backup {
  format: typeof BACKUP_FORMAT;
  schemaVersion: number;
  exportedAt: ISODateTime;
  data: {
    apiaries: Apiary[];
    colonies: Colony[];
    queens: Queen[];
    events: ColonyEvent[];
    products: Product[];
    settings: Setting[];
  };
}
